// Styles de la modale d'aperçu et de ses satellites (panneau de réglages,
// barre d'ajustement de la lettre, popover de retouche).
//
// Exportés comme CHAÎNE et non comme fichier .css : l'extension les injecte
// dans un shadow DOM (elle ne peut pas compter sur une feuille de style de la
// page hôte), la console les insère dans une balise <style>. Une seule source
// pour les deux, sinon les apparences divergent au premier ajustement.
//
// Les variables --s-* sont fournies par l'app hôte (thème du widget côté
// extension, thème de la console côté console).

export const previewCss = `
/* === Prévisualisation modale (§2.3) ============================== */
.preview-modal-backdrop {
  position: fixed;
  inset: 0;
  /* Au-dessus de la navigation de Radar (barre du bas : 30), sous ses
     fenêtres de questions (50) et la fenêtre des angles (60). */
  z-index: 45;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  animation: fadeIn 0.2s ease-out;
  font-family: -apple-system, system-ui, sans-serif;
}
.preview-modal {
  position: relative;
  background: var(--s-surface);
  border-radius: 14px;
  width: min(900px, 95vw);
  height: min(92vh, 950px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0,0,0,0.32);
}
/* Variante INTÉGRÉE (console) : le même composant vit dans la page, sans fond
   assombri ni fenêtre flottante. Il prend la place qu'on lui donne, d'où la
   neutralisation de la taille fixe et de l'ombre portée de la modale. */
.preview-modal-embed {
  display: flex;
  min-height: 0;
  font-family: -apple-system, system-ui, sans-serif;
}
.preview-modal-embed > .preview-modal {
  width: 100%;
  height: min(78vh, 1000px);
  box-shadow: none;
  border: 1px solid var(--s-line);
}
/* En-tête simple (encore utilisé par la modale de questions d'écart). */
.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid var(--s-line);
  flex-shrink: 0;
}
/* Barre d'outils unique : identité à gauche, actions transverses à droite.
   Remplace l'empilement titre / onglets / fidélité / indice. */
.preview-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 10px 14px;
  border-bottom: 1px solid var(--s-line);
  flex-shrink: 0;
}
.preview-toolbar-spacer { flex: 1; }
.preview-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 14px;
  color: var(--s-ink);
}
/* Chip d'action dans la barre (comparer, réglages). */
.preview-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border: 1px solid var(--s-line);
  background: var(--s-surface);
  color: var(--s-ink-2);
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color .15s, color .15s, background-color .15s;
}
.preview-chip:hover { border-color: var(--s-accent); color: var(--s-accent); }
.preview-chip.on { background: var(--s-accent-tint); border-color: var(--s-accent); color: var(--s-accent); }
.preview-close {
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: var(--s-ink-3);
  display: inline-grid;
  place-items: center;
}
.preview-close:hover:not(:disabled) { background: var(--s-surface-2); color: var(--s-ink); }

/* === Barre d'ajustement libre de la lettre (façon ChatGPT) ========== */
.preview-refine {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 20px;
  border-top: 1px solid var(--s-line);
  background: var(--s-surface-3);
  flex-shrink: 0;
}
.preview-refine-input {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid var(--s-line-strong);
  border-radius: 999px;
  font-size: 13px;
  font-family: inherit;
  color: var(--s-ink);
  outline: none;
}
.preview-refine-input:focus { border-color: var(--s-accent); }
.preview-refine-input:disabled { opacity: 0.6; cursor: not-allowed; }
.preview-refine-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--s-indigo), var(--s-accent));
  color: var(--s-surface);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.preview-refine-btn:disabled { opacity: 0.5; cursor: not-allowed; }
/* Reposer les questions (issue #166) : secondaire, à gauche du champ. */
.preview-questions-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid var(--s-line-strong);
  border-radius: 999px;
  background: var(--s-surface);
  color: var(--s-ink);
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.preview-questions-btn:hover:not(:disabled) { border-color: var(--s-accent); }
.preview-questions-btn:disabled { opacity: 0.5; cursor: not-allowed; }
/* Chiffres absents du profil (issue #160) : une ligne, avant la barre. */
.preview-numbers {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 20px;
  border-top: 1px solid var(--s-line);
  background: var(--s-surface-3);
  font-size: 12px;
  color: var(--s-ink-2);
  flex-shrink: 0;
}
.preview-numbers svg { margin-top: 2px; flex-shrink: 0; color: var(--s-warn); }
.preview-numbers strong { color: var(--s-ink); cursor: help; }
/* Questions d'angle avant la lettre (issue #165). Par-dessus l'aperçu.
   \`pointer-events: auto\` : le conteneur de l'extension coupe les clics, chaque
   fenêtre doit les rétablir. Sans lui, aucun clic ne passait (issue #176). */
.angles-backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  pointer-events: auto;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: -apple-system, system-ui, sans-serif;
}
.angles-modal {
  width: min(560px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  background: var(--s-surface);
  color: var(--s-ink);
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}
.angles-title { margin: 0 0 4px; font-size: 16px; font-weight: 700; }
.angles-intro { margin: 0 0 14px; font-size: 13px; color: var(--s-ink-2); }
.angles-question { margin-bottom: 14px; }
.angles-label { font-size: 13px; font-weight: 600; margin-bottom: 6px; }
.angles-options { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
.angles-option {
  padding: 6px 10px;
  border: 1px solid var(--s-line-strong);
  border-radius: 999px;
  background: var(--s-surface);
  color: var(--s-ink);
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
}
.angles-option.on { border-color: var(--s-accent); color: var(--s-accent); font-weight: 600; }
.angles-input {
  width: 100%;
  box-sizing: border-box;
  padding: 7px 10px;
  border: 1px solid var(--s-line-strong);
  border-radius: 8px;
  background: var(--s-surface);
  color: var(--s-ink);
  font-size: 13px;
  font-family: inherit;
}
.angles-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 6px; }
.angles-skip {
  padding: 8px 14px;
  border: 1px solid var(--s-line-strong);
  border-radius: 999px;
  background: var(--s-surface);
  color: var(--s-ink);
  font-size: 13px;
  cursor: pointer;
}
.angles-write {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 0;
  border-radius: 999px;
  background: var(--s-accent-fill);
  color: var(--s-on-accent);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.angles-write:disabled, .angles-skip:disabled { opacity: 0.5; cursor: not-allowed; }
/* Ce que la génération a lu : replié, une ligne sous la barre. */
.preview-facts {
  padding: 0 20px 10px;
  background: var(--s-surface-3);
  font-size: 12px;
  color: var(--s-ink-2);
  flex-shrink: 0;
  max-height: 160px;
  overflow-y: auto;
}
.preview-facts summary { cursor: pointer; }
.preview-facts ul { margin: 6px 0 0; padding-left: 18px; }
.preview-facts li { margin: 2px 0; }
.preview-facts p { margin: 6px 0 0; }

/* Onglets CV / Lettre (Phase 4) — segmented compact dans la barre d'outils. */
.preview-tabs {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  background: var(--s-surface-2);
  border-radius: 10px;
}
.preview-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 0;
  background: transparent;
  color: var(--s-ink-2);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}
.preview-tab:hover { color: var(--s-ink); }
.preview-tab.active {
  background: var(--s-surface);
  color: var(--s-accent);
  box-shadow: 0 1px 3px rgba(20,32,55,0.12);
}

/* Historique des versions de la lettre. */
.preview-versions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px 20px;
  background: var(--s-surface-3);
  border-bottom: 1px solid var(--s-line);
  color: var(--s-ink-3);
  flex-shrink: 0;
}
.preview-versions-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-right: 2px;
}
.preview-version {
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid var(--s-line-strong);
  border-radius: 6px;
  background: var(--s-surface);
  color: var(--s-ink-3);
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, border-color 0.15s;
}
.preview-version:hover:not(:disabled) { border-color: var(--s-accent); color: var(--s-accent); }
.preview-version.active {
  background: var(--s-accent);
  border-color: var(--s-accent);
  color: var(--s-surface);
}
.preview-version:disabled { opacity: 0.5; cursor: not-allowed; }
.preview-versions-current {
  font-size: 11px;
  font-style: italic;
  color: var(--s-ink-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 45%;
}

/* Ce qui a changé, en une phrase (issue #178). Neutre : une couleur d'alerte
   sur un chiffre faussé par une traduction faisait croire à une perte. */
.preview-changes {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 20px;
  border-bottom: 1px solid var(--s-line);
  font-size: 13px;
  color: var(--s-ink-2);
  flex-shrink: 0;
}
/* La mise en page, repliée sous la rédaction. */
.preview-settings-layout { margin-top: 6px; }
.preview-settings-layout > summary { cursor: pointer; list-style: none; display: flex; align-items: center; gap: 6px; }
.preview-settings-layout > summary::-webkit-details-marker { display: none; }
.preview-settings-layout > summary::before { content: '▸'; font-size: 10px; }
.preview-settings-layout[open] > summary::before { content: '▾'; }
.preview-settings-default {
  display: block;
  margin: 8px auto 0;
  border: 0;
  background: none;
  color: var(--s-accent);
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
.preview-settings-default:disabled { opacity: 0.5; cursor: not-allowed; }
.preview-changes svg { margin-top: 2px; flex-shrink: 0; color: var(--s-accent); }

.preview-body {
  flex: 1;
  background: var(--s-surface-2);
  padding: 16px;
  /* hidden (et non auto) : le document défile DANS son iframe. Sinon le corps
     scrollerait et emporterait le panneau de réglages (positionné en absolu). */
  overflow: hidden;
  min-height: 0;
  display: flex;
  gap: 12px;
  align-items: stretch;
  position: relative; /* ancre l'overlay du loader IA + le panneau glissant */
}
.preview-iframe {
  flex: 1 1 0%;
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
  border: 0;
  border-radius: 6px;
  background: var(--s-surface);
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

/* === Panneau de réglages unifié (CV + lettre) ===================== */
/* Voile derrière le panneau glissant : ferme au clic, hors du panneau. */
.preview-settings-scrim {
  position: absolute;
  inset: 0;
  z-index: 8;
  background: rgba(20, 32, 55, 0.28);
  animation: fadeIn 0.2s ease-out;
}
/* Panneau de réglages : glisse depuis la droite au lieu d'écraser le document. */
.preview-settings {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 9;
  width: min(340px, 88%);
  display: flex;
  flex-direction: column;
  background: var(--s-surface);
  color: var(--s-ink);
  box-shadow: -18px 0 48px rgba(20, 32, 55, 0.20);
  animation: settingsSlideIn 0.24s cubic-bezier(.4, 0, .2, 1);
}
@keyframes settingsSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
.preview-settings-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px 12px;
  border-bottom: 1px solid var(--s-line-2);
  flex-shrink: 0;
}
.preview-settings-scroll { flex: 1; overflow: auto; padding: 4px 18px 12px; }
.preview-settings-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-weight: 700;
  font-size: 15px;
  color: var(--s-ink);
}
/* Badge indiquant le document réglé (CV / Lettre). */
.preview-settings-doc {
  font-size: 11px;
  font-weight: 700;
  color: var(--s-accent);
  background: var(--s-accent-tint);
  border: 1px solid var(--s-accent-border);
  border-radius: 999px;
  padding: 2px 9px;
}
/* Titre de groupe : « Mise en page » / « Rédaction IA ». */
.preview-settings-group-title {
  margin: 16px 0 12px 0;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--s-accent);
}
.preview-settings-group-title:first-of-type { margin-top: 10px; }
.preview-settings-group { margin-bottom: 18px; }
.preview-settings-label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: var(--s-ink);
  margin-bottom: 8px;
}
.preview-settings-segmented {
  display: flex;
  gap: 6px;
}
.preview-settings-segmented.wrap { flex-wrap: wrap; }
.preview-settings-segmented.wrap .preview-seg { flex: 1 0 calc(50% - 6px); }
.preview-seg {
  flex: 1 1 auto;
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid var(--s-line);
  background: var(--s-surface);
  border-radius: 9px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  color: var(--s-ink-2);
  transition: border-color .15s, background-color .15s, color .15s;
}
.preview-seg:hover:not(:disabled):not(.active) { border-color: var(--s-line-strong); }
.preview-seg:disabled { opacity: 0.55; cursor: not-allowed; }
.preview-seg.active {
  border-color: var(--s-accent);
  background: var(--s-accent-tint);
  color: var(--s-accent);
  font-weight: 600;
}
/* Cran « Audacieux » : signalé en ambre (extrapolation assumée). */
.preview-seg.daring.active {
  border-color: var(--s-warn-fill);
  background: var(--s-warn-tint);
  color: var(--s-warn);
}
/* Phrase d'explication sous un réglage. */
.preview-settings-explain {
  margin: 8px 0 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--s-ink-3);
}
.preview-settings-explain.warn {
  color: var(--s-warn);
  font-weight: 600;
}
/* Confirmation « Audacieux » intégrée (remplace window.confirm). */
.preview-daring-confirm {
  margin-top: 10px;
  padding: 11px 12px;
  background: var(--s-warn-tint);
  border: 1px solid var(--s-warn-soft);
  border-radius: 10px;
  animation: fadeIn 0.18s ease-out;
}
.preview-daring-confirm p {
  margin: 0 0 10px;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--s-warn);
}
.preview-daring-actions { display: flex; gap: 8px; }
.preview-daring-actions button {
  flex: 1;
  min-height: 34px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}
.preview-daring-yes { background: var(--s-warn-fill); color: var(--s-surface); }
.preview-daring-yes:hover { background: var(--s-warn); }
.preview-daring-no { background: var(--s-surface); border-color: var(--s-line); color: var(--s-ink-2); }
.preview-daring-no:hover { background: var(--s-surface-2); }
.preview-settings-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.preview-swatch {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
}
.preview-swatch.active {
  border-color: var(--s-ink);
  box-shadow: 0 0 0 2px var(--s-surface) inset;
}
/* Catalogue de modèles (Radar, issue #235) : des vignettes du vrai CV, deux
   par ligne, qui passent à la ligne au lieu de défiler. Le modèle choisi se lit
   au contour ET à la coche, pas à la couleur seule. */
.preview-modeles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}
.preview-modele {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 6px;
  border: 1.5px solid var(--s-line);
  border-radius: 10px;
  background: var(--s-surface);
  color: var(--s-ink-2);
  font-family: inherit;
  font-size: 12.5px;
  text-align: left;
  cursor: pointer;
  min-height: 44px;
}
.preview-modele:hover:not(:disabled) { border-color: var(--s-ink-3); }
.preview-modele:focus-visible { outline: 2px solid var(--s-accent); outline-offset: 2px; }
.preview-modele.active { border-color: var(--s-accent); color: var(--s-ink); font-weight: 600; box-shadow: 0 0 0 1px var(--s-accent); }
.preview-modele:disabled { cursor: default; opacity: 0.6; }
.preview-modele-vue {
  position: relative;
  display: block;
  height: 132px;
  overflow: hidden;
  border-radius: 6px;
  background: #fff;
  border: 1px solid var(--s-line-2);
}
/* Le CV est rendu à sa vraie largeur (210 mm) puis réduit : la vignette montre
   le haut de la première page, comme une feuille posée. */
.preview-modele-vue iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 794px;
  height: 1123px;
  border: 0;
  transform: scale(0.175);
  transform-origin: top left;
  pointer-events: none;
}
.preview-modele-nom { display: flex; align-items: center; gap: 4px; }
.preview-modele-etat { font-size: 11.5px; font-weight: 500; color: var(--s-ink-3); }
.preview-modele.verrou .preview-modele-vue { opacity: 0.55; }
.preview-modeles-couleurs { margin: 4px 0 10px; }
.preview-modeles-defaut {
  border: 0;
  padding: 0;
  background: none;
  color: var(--s-accent);
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.preview-settings-check {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  color: var(--s-ink-2);
  margin-bottom: 10px;
  cursor: pointer;
}
.preview-settings-check input { cursor: pointer; margin: 0; width: 16px; height: 16px; accent-color: var(--s-accent); }
/* Pied du panneau : LE bouton de régénération (unique). */
.preview-settings-foot {
  flex-shrink: 0;
  padding: 14px 18px;
  border-top: 1px solid var(--s-line-2);
  background: var(--s-surface-3);
}
.preview-settings-apply {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border: 0;
  border-radius: 11px;
  background: var(--s-accent);
  color: var(--s-surface);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(10, 102, 194, 0.3);
}
.preview-settings-apply:hover:not(:disabled) { background: var(--s-accent-hover); }
.preview-settings-apply:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; }
.preview-settings-hint {
  margin: 9px 2px 0;
  font-size: 11.5px;
  color: var(--s-ink-3);
  line-height: 1.4;
  text-align: center;
}

/* Indice d'édition : pastille flottante, affichée une seule fois. */
.preview-edit-hint {
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: calc(100% - 32px);
  padding: 8px 8px 8px 14px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--s-surface);
  background: var(--s-ink);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(20,32,55,0.28);
  animation: fadeIn 0.2s ease-out;
}
.preview-edit-hint-close {
  flex-shrink: 0;
  border: 0;
  background: rgba(255,255,255,0.16);
  color: var(--s-surface);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s;
}
.preview-edit-hint-close:hover { background: rgba(255,255,255,0.28); }

.preview-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid var(--s-line);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.preview-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
/* Lien « noter l'extension » (chantier E) : discret, poussé à gauche. */
.preview-rate-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-right: auto;
  font-size: 12px;
  color: var(--s-ink-3);
  text-decoration: none;
}
.preview-rate-link:hover { color: var(--s-accent); text-decoration: underline; }
/* Pourquoi les exports sont grisés : la phrase occupe la place du lien de
   notation, à gauche des actions, pour être lue avant de cliquer. */
.preview-save-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: auto;
  font-size: 12px;
  color: var(--s-ink-2);
}
.preview-save-note.err { color: var(--s-danger); }
/* Bandeau « la lettre a vieilli » — au-dessus de la barre d'ajustement, donc
   dans le champ de vision de qui s'apprête à exporter. Ton d'avertissement, pas
   d'erreur : rien n'est cassé, c'est le document qui a bougé sous la lettre. */
.preview-stale {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-size: 12.5px;
  color: var(--s-ink-2);
  background: var(--s-surface-2);
  border-top: 1px solid var(--s-line);
}
.preview-stale-btn {
  margin-left: auto;
  flex: none;
  padding: 4px 12px;
  border: 1px solid var(--s-line);
  border-radius: 999px;
  background: var(--s-surface);
  color: var(--s-ink);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.preview-stale-btn:hover:not(:disabled) { border-color: var(--s-accent); color: var(--s-accent); }
.preview-stale-btn:disabled { opacity: .55; cursor: not-allowed; }
.preview-btn-secondary, .preview-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 13px;
  border: 0;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}
.preview-btn-secondary {
  background: var(--s-surface-2);
  color: var(--s-ink);
}
.preview-btn-secondary:hover:not(:disabled) { background: var(--s-line); }
.preview-btn-primary {
  background: var(--s-accent);
  color: var(--s-surface);
}
.preview-btn-primary:hover:not(:disabled) { background: var(--s-accent-hover); }
.preview-btn-primary:disabled, .preview-btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
/* Anneau de focus visible : la navigation clavier doit voir où elle est. */
.preview-btn-primary:focus-visible,
.preview-btn-secondary:focus-visible,
.preview-close:focus-visible {
  outline: 2px solid var(--s-accent);
  outline-offset: 2px;
}
.spin { animation: spin 0.9s linear infinite; }

/* === Popover de retouche IA ad-hoc (Phase 5) ========================== */
.revision-trigger {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: linear-gradient(135deg, var(--s-indigo), var(--s-accent));
  color: var(--s-surface);
  border: none;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(10, 102, 194, 0.32);
  font-family: -apple-system, system-ui, sans-serif;
  pointer-events: auto;
}
.revision-trigger:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(10, 102, 194, 0.40); }
.revision-trigger:disabled { opacity: 0.6; cursor: wait; }

.revision-popover {
  width: 360px;
  background: var(--s-surface);
  border-radius: 12px;
  box-shadow: 0 12px 36px rgba(0,0,0,0.20);
  border: 1px solid var(--s-line);
  padding: 12px;
  font-family: -apple-system, system-ui, sans-serif;
  font-size: 12px;
  color: var(--s-ink);
  pointer-events: auto;
}
.revision-popover-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.revision-popover-title {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  color: var(--s-indigo-strong);
}
.revision-popover-close {
  background: transparent;
  border: 0;
  padding: 2px;
  cursor: pointer;
  color: var(--s-ink-3);
  border-radius: 4px;
}
.revision-popover-close:hover { background: var(--s-surface-2); color: var(--s-ink); }
.revision-popover-selection {
  font-style: italic;
  color: var(--s-ink-3);
  margin: 0 0 10px 0;
  max-height: 60px;
  overflow: hidden;
  line-height: 1.4;
}
.revision-popover-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 10px;
}
.revision-preset {
  padding: 4px 9px;
  border: 1px solid var(--s-line-strong);
  background: var(--s-surface);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  color: var(--s-ink-2);
}
.revision-preset:hover:not(:disabled) { background: var(--s-surface-2); }
.revision-preset:disabled { opacity: 0.5; cursor: not-allowed; }
.revision-popover-form {
  display: flex;
  gap: 6px;
}
.revision-popover-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid var(--s-line-strong);
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  font-family: inherit;
}
.revision-popover-input:focus { border-color: var(--s-accent); }
.revision-popover-submit {
  padding: 6px 12px;
  background: var(--s-accent);
  color: var(--s-surface);
  border: 0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.revision-popover-submit:disabled { opacity: 0.5; cursor: not-allowed; }
.revision-popover-error {
  margin: 8px 0 0 0;
  padding: 6px 8px;
  background: var(--s-danger-tint);
  color: var(--s-danger);
  border-radius: 6px;
  font-size: 11px;
}

/* Au téléphone (issue #178) : une colonne. La barre d'outils et la barre de
   consigne passent à la ligne au lieu d'élargir l'atelier (633 px dans un
   écran de 356, mesuré), le panneau de réglages prend toute la largeur, et la
   bulle « cliquez dans le CV » disparaît : on ne retouche pas un CV au doigt. */
.preview-modal { min-width: 0; }
@media (max-width: 600px) {
  .preview-modal-embed { min-width: 0; }
  .preview-modal-embed > .preview-modal { height: 82vh; }
  .preview-toolbar { flex-wrap: wrap; padding: 8px 10px; }
  .preview-toolbar-spacer { flex-basis: 100%; height: 0; }
  .preview-changes { padding: 8px 12px; }
  .preview-refine { flex-wrap: wrap; padding: 10px 12px; }
  .preview-refine-input { flex-basis: 100%; order: -1; }
  .preview-footer { padding: 10px 12px; gap: 8px; }
  .preview-actions { flex-wrap: wrap; justify-content: flex-end; }
  .preview-settings { width: 100%; }
  .preview-edit-hint { display: none; }
  /* La fenêtre devient un écran : plus de marge ni d'angles arrondis. */
  .preview-modal-backdrop { padding: 0; }
  .preview-modal-backdrop > .preview-modal { width: 100vw; height: 100dvh; border-radius: 0; }
}
`
